/**
 * PushNotificationPrompt Component
 *
 * A card component that prompts users to enable push notifications.
 * Shows only when:
 * - Push notifications are supported
 * - User is logged in
 * - User hasn't enabled notifications yet
 * - Permission hasn't been denied
 */

"use client";

import { useAuth } from "@/contexts/AuthContext";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { Button, Card, CardBody } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslations } from "next-intl";

interface PushNotificationPromptProps {
  /** Additional CSS classes */
  className?: string;
  /** Whether to show as a compact banner instead of a card */
  variant?: "card" | "banner";
  /** Callback when dismissed (optional) */
  onDismiss?: () => void;
}

export function PushNotificationPrompt({
  className = "",
  variant = "card",
  onDismiss,
}: PushNotificationPromptProps) {
  const t = useTranslations();
  const { user } = useAuth();
  const { isSupported, permission, isEnabled, isLoading, enableNotifications } =
    usePushNotifications();

  // Don't show if not supported, not logged in, already enabled, or denied
  if (!isSupported || !user || isEnabled || permission === "denied") {
    return null;
  }

  const handleEnable = async () => {
    await enableNotifications();
  };

  if (variant === "banner") {
    return (
      <div
        className={`flex items-center justify-between gap-4 rounded-lg bg-primary-50 dark:bg-primary-900/20 p-4 ${className}`}
        role="alert"
      >
        <div className="flex items-center gap-3">
          <Icon
            icon="solar:bell-bold"
            className="h-5 w-5 text-primary"
            aria-hidden="true"
          />
          <p className="text-sm text-foreground">
            {t("notifications.enablePromptBanner")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            color="primary"
            onPress={handleEnable}
            isLoading={isLoading}
            aria-label={t("notifications.enableButton")}
          >
            {t("notifications.enableButton")}
          </Button>
          {onDismiss && (
            <Button
              size="sm"
              variant="light"
              isIconOnly
              onPress={onDismiss}
              aria-label={t("common.dismiss")}
            >
              <Icon icon="solar:close-circle-linear" className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <Card className={className}>
      <CardBody className="flex flex-col items-center gap-4 p-6 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
          <Icon
            icon="solar:bell-bold"
            className="h-6 w-6 text-primary"
            aria-hidden="true"
          />
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">
            {t("notifications.enablePromptTitle")}
          </h3>
          <p className="text-sm text-default-500">
            {t("notifications.enablePromptDescription")}
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            color="primary"
            onPress={handleEnable}
            isLoading={isLoading}
            startContent={
              !isLoading && <Icon icon="solar:bell-bold" className="h-4 w-4" />
            }
          >
            {t("notifications.enableButton")}
          </Button>
          {onDismiss && (
            <Button variant="flat" onPress={onDismiss}>
              {t("common.notNow")}
            </Button>
          )}
        </div>

        <p className="text-xs text-default-400">
          {t("notifications.enablePromptNote")}
        </p>
      </CardBody>
    </Card>
  );
}
